export enum ShiftStatusWithdrawAllowed {
    PunchclockFinished = "PunchclockFinished",
    Registered = "Registered",
    Approved = "Approved",
}

export interface BalanceDetails {
    earned: number;
    withholdings: number;
    earnedAfterWithholdings: number;
    additionalAdjustments: number;
    withdrawed: number;
    withdrawalFees: number;
    allowancePercentage: number;
    allowancePercentageDeduction: number;
    start: string;
    end: string;
    payrollPeriodStart: string;
    payrollPeriodEnd: string;
    workdays: number;
    totalBusinessDaysInMonth: number;
    workedHoursPercent: number;
    mileageKm: number;
    mileageTotal: number;
    hasMileageRecordsTripDetails: boolean;
    pendingWithdrawals: number;
    unapprovedHoursWithdrawAllowed: boolean;
    registeredHours: number;
    approvedHours: number;
    availableShiftsTotalCount: number;
    upcomingShiftsTotalHours: number;
    employmentType?: "Hourly" | "Fixed";
    /** Effective product for this employee, derived from their employment type. "AllProducts" when no employment type is set. */
    product?: "AllProducts" | "TipsOnlyViewAndWithdraw" | "TipsOnlyView";
    shiftStatusWithdrawAllowed: ShiftStatusWithdrawAllowed;
    shiftStatusTipsWithdrawAllowed: ShiftStatusWithdrawAllowed;
    unavailableShiftsTotalAmount: number;
    upcomingShiftsTotalAmount: number;
    availableShiftsTotalAmount: number;
    pendingWithdrawalsCount: number;
    hoursSummary: {
        registered: number;
        punchclockStarted: number;
        punchclockFinished: number;
        approved: number;
    };
    calculationsDetails: {
        earningsBeforeTax: number;
        amContributionAfterATP: number;
        earningsAfterTax: number;
        monthlySalary: number | null;
        monthlyNumberOfHours: number | null;
        taxDeduction: number;
        atpContribution: number;
        appliedTaxDeduction: number;
        taxDeductionBlocked: boolean;
        blockedTaxDeduction: number;
        amContribution: number;
        aIncome: number;
        taxableIncome: number;
        taxRate: number;
        incomeTax: number;
        atpContributionBracket: string;
    };
    balanceAdjustments: {
        totalAdjustmentsBeforeTax: number;
        totalAdjustmentsAfterAM: number;
        totalAdjustmentsAfterTax: number;
    };
    tipBalance: {
        totalTipsBeforeTax: number;
        lastEarnedTip: number;
    };
}

export interface Theme {
    id: string;
    name: string;
    logoName: string;
    colors: any;
}

export interface CompanyResponse {
    allowancePercentage: number;
    latestWithdrawalDay: any;
    withdrawalsLocked: boolean;
    shiftStatusWithdrawAllowed?: ShiftStatusWithdrawAllowed;
    shiftStatusTipsWithdrawAllowed?: ShiftStatusWithdrawAllowed;
    companyId: string;
    createdAt: Date;
    name: string;
    currency: string;
    greetingText: string;
    theme: Theme;
    loginType: LoginType;
    endOfTrial: Date | null;
    timeRegistrationProvider: string;
    requiredConsentsAccepted: boolean;
    optionalConsentsAvailable: boolean;
    showReports: boolean;
    ianaTimeZone?: string;
    enableTips: boolean;
    employeeWithdrawalFee: number;
    minimumWithdrawalAmount: number;
    balanceSummaryTextDa: string;
    balanceSummaryTextDe: string;
    balanceSummaryTextEn: string;
    [key: `balanceSummaryText${string}`]: string | undefined;
}

export enum LoginType {
    AzureB2C = "AzureB2C",
    AzureAD = "AzureAD",
}

export interface BankDetailsResponse {
    bankCode?: string | null;
    accountNumber?: string | null;
    cprNumber?: string;
    isNemkonto?: boolean;
    /**
     * US-3052 / Feature 3046. Pre-resolved payout destination string, computed
     * server-side via `PayoutRouter.Resolve`. Render verbatim when non-null
     * (BBAN as `"1234 - 1234567890"`, IBAN as ISO 13616 4-group). When `null`,
     * fall back to the existing NemKonto / missing-bank treatment — never
     * localise this string FE-side. This is the **primary** display gate;
     * `isNemkonto` is only consulted on the null-fallback path.
     */
    payoutDestinationDisplay?: string | null;
    /**
     * US-3052 / Feature 3046. BE-supplied flag indicating whether *any*
     * payout receiver (BBAN, IBAN, or NemKonto) is on file for the user.
     * Source of truth for the withdraw-gate — PayoutRouter has resolved
     * server-side whether a usable receiver exists. Required because BE +
     * Backoffice are deployed together in maintenance mode (no rollout
     * window where the field could be absent). Backoffice consumers that
     * don't get this from BE must compute it at the call site.
     */
    hasBankDetails: boolean;
    /**
     * US-3191. True when `hasBankDetails` is false specifically because the employee's
     * IBAN destination country is not on the SEPA supported list (e.g. Switzerland or
     * any non-SEPA country). Show a distinct, actionable message instead of the generic
     * "bank details not available" copy when this flag is set.
     */
    isUnsupportedDestinationCountry: boolean;
}

export interface PendingTransactionsResponse {
    pendingTransactionsCount: number;
}
